import { Box, Container, Grid, Typography, Divider, TextField, Button, Stack, Alert, CircularProgress } from "@mui/material";
import HomeCard from "./HomeCard";
import HomeCardSub from "./HomeCardSub";
import { theme } from "./theme";
import useTranslationState from "./useTranslationState";
import { useState } from "react";
import { ContactForm2EndpointPostPromise } from "../routes/ContactForm";
import ContactFormResponse from "../responses/ContactForm";

function Home3() {
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setContactForm({
            ...contactForm,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const promise = ContactForm2EndpointPostPromise(
                contactForm.name,
                contactForm.email,
                contactForm.message
            );

            promise.then((rawResponse) => {
                rawResponse.json().then((response: ContactFormResponse) => {
                    if (response.data.success) {
                        setFormSubmitted(true);
                        setContactForm({ name: '', email: '', message: '' });

                        // Reset the success message after 5 seconds
                        setTimeout(() => {
                            setFormSubmitted(false);
                        }, 5000);
                    } else {
                        setError(response.data.message || 'Failed to send message');
                    }
                });
            });
        } catch (err) {
            setError('An error occurred. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    const translations = useTranslationState({
        whatWeOffer: '',
        privateChefs: '',
        hireAPrivateChef: '',
        privateChefBigText: '',
        consulting: '',
        getConsulting: '',
        consultingBigText: '',
        rentalChefs: '',
        getRentalChef: '',
        rentalChefBigText: '',
        conception: '',
        conceptionText: '',
        conceptionBigText: '',
        kitchenPlanning: '',
        kitchenPlanningText: '',
        kitchenPlanningBigText: '',
        menuPlanning: '',
        menuPlanningText: '',
        menuPlanningBigText: '',
        qualityManagement: '',
        qualityManagementText: '',
        qualityManagementBigText: '',
        diningRoomDesign: '',
        diningRoomDesignText: '',
        diningRoomDesignBigText: '',
        readMore: '',
        aboutServices: '',
        servicesDescription: '',
        contactUs: '',
        contactDescription: '',
        yourName: 'Your Name',
        yourEmail: 'Your Email',
        yourMessage: 'Your Message',
        send: 'Send Message',
        thankYou: 'Thank you! We will get back to you soon.',
    });

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: theme.palette.primary.dark,
                paddingBottom: 16,
            }}
        >
            <Container
                maxWidth="lg"
                sx={{
                    py: 8,
                    textAlign: 'center',
                }}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                        color: 'white',
                        textAlign: 'center',
                        marginBottom: 2,
                        fontWeight: 500,
                        letterSpacing: '-0.5px',
                    }}
                >
                    {translations.whatWeOffer}
                </Typography>

                <Divider
                    sx={{
                        width: '60px',
                        margin: '0 auto',
                        marginBottom: 6,
                        borderColor: 'rgba(255,255,255,0.3)',
                    }}
                />

                <Grid
                    container
                    spacing={4}
                    sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'center',
                    }}
                >
                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <HomeCard
                            title={translations.privateChefs}
                            text={translations.hireAPrivateChef}
                            image="/img/suppe.JPG"
                            translations={translations}
                            link="/private-chefs"
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <HomeCard
                            title={translations.rentalChefs}
                            text={translations.getRentalChef}
                            image="/img/hauptgang.JPG"
                            translations={translations}
                            link="/rental-chefs"
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <HomeCard
                            title={translations.consulting}
                            text={translations.getConsulting}
                            image="/img/dessert.JPG"
                            translations={translations}
                            link="/consulting"
                        />
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        mt: 12,
                        mb: 6,
                        maxWidth: "800px",
                        mx: "auto",
                        px: 3,
                        opacity: 0,
                        animation: 'fadeIn 1s forwards cubic-bezier(0.23, 1, 0.32, 1)',
                        animationDelay: '0.6s',
                        '@keyframes fadeIn': {
                            '0%': {
                                opacity: 0,
                                transform: 'translateY(10px)',
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'translateY(0)',
                            },
                        },
                    }}
                >
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            color: 'white',
                            textAlign: 'center',
                            mb: 3,
                            fontWeight: 400,
                        }}
                    >
                        {translations.aboutServices}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(255,255,255,0.85)',
                            textAlign: 'center',
                            mb: 4,
                            lineHeight: 1.8,
                            fontSize: "1.05rem",
                        }}
                    >
                        {translations.servicesDescription}
                    </Typography>
                    <Divider
                        sx={{
                            width: '40px',
                            margin: '0 auto',
                            my: 6,
                            borderColor: 'rgba(255,255,255,0.2)',
                        }}
                    />
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            color: 'white',
                            textAlign: 'center',
                            mb: 3,
                            fontWeight: 400,
                        }}
                    >
                        {translations.contactUs}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(255,255,255,0.85)',
                            textAlign: 'center',
                            lineHeight: 1.8,
                            fontSize: "1.05rem",
                            mb: 6,
                        }}
                    >
                        {translations.contactDescription}
                    </Typography>

                    {/* Contact Form */}
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            maxWidth: 500,
                            mx: "auto",
                            mt: 4,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            borderRadius: '16px',
                            p: { xs: 3, sm: 5 },
                            border: '1px solid rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(10px)',
                            opacity: 0,
                            animation: 'fadeIn 1s forwards cubic-bezier(0.23, 1, 0.32, 1)',
                            animationDelay: '0.8s',
                        }}
                    >
                        <Stack spacing={3}>
                            {formSubmitted && (
                                <Alert
                                    severity="success"
                                    sx={{
                                        borderRadius: '8px',
                                        backgroundColor: 'rgba(46, 125, 50, 0.1)',
                                        color: '#4caf50',
                                        border: '1px solid rgba(76, 175, 80, 0.2)',
                                    }}
                                >
                                    {translations.thankYou}
                                </Alert>
                            )}

                            {error && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        borderRadius: '8px',
                                        backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                        color: '#f44336',
                                        border: '1px solid rgba(244, 67, 54, 0.2)',
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}

                            <TextField
                                name="name"
                                value={contactForm.name}
                                onChange={handleFormChange}
                                required
                                fullWidth
                                label={translations.yourName}
                                variant="outlined"
                                disabled={isLoading}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        color: "white",
                                        borderRadius: "8px",
                                        "& fieldset": {
                                            borderColor: "rgba(255,255,255,0.2)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "rgba(255,255,255,0.4)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "white",
                                        },
                                        "& .MuiInputBase-input": {
                                            color: "white",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "rgba(255,255,255,0.7)",
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "white",
                                    },
                                }}
                            />
                            <TextField
                                name="email"
                                value={contactForm.email}
                                onChange={handleFormChange}
                                required
                                fullWidth
                                label={translations.yourEmail}
                                type="email"
                                variant="outlined"
                                disabled={isLoading}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        color: "white",
                                        borderRadius: "8px",
                                        "& fieldset": {
                                            borderColor: "rgba(255,255,255,0.2)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "rgba(255,255,255,0.4)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "white",
                                        },
                                        "& .MuiInputBase-input": {
                                            color: "white",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "rgba(255,255,255,0.7)",
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "white",
                                    },
                                }}
                            />
                            <TextField
                                name="message"
                                value={contactForm.message}
                                onChange={handleFormChange}
                                required
                                fullWidth
                                label={translations.yourMessage}
                                multiline
                                rows={5}
                                variant="outlined"
                                disabled={isLoading}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        color: "white",
                                        borderRadius: "8px",
                                        "& fieldset": {
                                            borderColor: "rgba(255,255,255,0.2)",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "rgba(255,255,255,0.4)",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "white",
                                        },
                                        "& .MuiInputBase-input": {
                                            color: "white",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "rgba(255,255,255,0.7)",
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "white",
                                    },
                                }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isLoading}
                                sx={{
                                    borderRadius: "8px",
                                    py: 1.5,
                                    textTransform: "none",
                                    fontWeight: 500,
                                    fontSize: "1rem",
                                    backgroundColor: "white",
                                    color: theme.palette.primary.dark,
                                    boxShadow: "none",
                                    transition: "all 0.3s",
                                    "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.9)",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    translations.send
                                )}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}

export default Home3; 
